/* eslint-disable react/prop-types, prettier/prettier */
import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { keyframes } from '@emotion/react'
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    CircularProgress,
    MenuItem,
    Chip,
} from '@mui/material'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import BarChartIcon from '@mui/icons-material/BarChart'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import EmailIcon from '@mui/icons-material/Email'
import SmsIcon from '@mui/icons-material/Sms'
import PolylineIcon from '@mui/icons-material/Polyline'
import AssessmentIcon from '@mui/icons-material/Assessment'
import AIService from '../../services/aiService'
import { useToolRun } from '../../helpers/toolRun'
import ErrorBanner from '../../components/shared/ErrorBanner'
import {
    ACCENT,
    BRAND,
    CATEGORY,
    GRADIENT,
    ICON,
    ON_ORANGE,
    RADIUS,
    STATUS,
    SURFACE,
    TEXT,
    cardSx,
    menuProps,
    pageSx,
    tint,
} from '../../theme/tokens'

const fadeIn = keyframes`from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}`
const shimmer = keyframes`0%{background-position:-400px 0}100%{background-position:400px 0}`

const CHANNELS = [
    { value: 'whatsapp', label: 'WhatsApp', Icon: WhatsAppIcon },
    { value: 'email', label: 'Email', Icon: EmailIcon },
    { value: 'sms', label: 'SMS', Icon: SmsIcon },
]

const METRIC_FIELDS = [
    { key: 'delivered', label: 'Delivered', placeholder: 'e.g. 9200' },
    { key: 'opened', label: 'Opened / Read', placeholder: 'e.g. 4100' },
    { key: 'clicked', label: 'Clicked', placeholder: 'e.g. 820' },
    { key: 'replied', label: 'Replied', placeholder: 'e.g. 340' },
    { key: 'unsubscribed', label: 'Unsubscribed', placeholder: 'e.g. 45' },
    { key: 'total_sent', label: 'Total Sent', placeholder: 'e.g. 10000' },
]

// Text-safe in both themes on the card and on the grade badge's tint
// (theme/contrast.ai-b.test.js).
export const GRADE_CONFIG = {
    A: { color: STATUS.success.fg, label: 'Excellent' },
    B: { color: CATEGORY.lime, label: 'Good' },
    C: { color: STATUS.warning.fg, label: 'Average' },
    D: { color: STATUS.error.fg, label: 'Needs Work' },
    F: { color: STATUS.error.fg, label: 'Poor' },
}

// An insight's impact: its STATUS pair (text, tint, border). Unknown
// impacts take the orange accent.
export const IMPACT_TONE = {
    positive: STATUS.success,
    neutral: STATUS.warning,
    negative: STATUS.error,
}
export const IMPACT_FALLBACK = {
    fg: ACCENT.text,
    bg: ACCENT.bg08,
    border: ACCENT.border30,
}

// Each rate keeps its own colour; the unsubscribe rate is the one bad one.
export const RATE_COLOR = {
    delivery: CATEGORY.cyan,
    open: CATEGORY.orange,
    click: CATEGORY.green,
    reply: CATEGORY.amber,
    unsubscribe: STATUS.error.fg,
}

function ShimmerBlock({ height = 80, borderRadius = RADIUS.lg }) {
    return (
        <Box
            sx={{
                height,
                borderRadius: `${borderRadius}px`,
                overflow: 'hidden',
                background: `linear-gradient(90deg, ${SURFACE.sunken} 25%, ${SURFACE.border} 50%, ${SURFACE.sunken} 75%)`,
                backgroundSize: '800px 100%',
                animation: `${shimmer} 1.5s infinite linear`,
            }}
        />
    )
}

function ScoreGauge({ score, grade }) {
    const cfg = GRADE_CONFIG[grade] || GRADE_CONFIG.C
    const size = 160
    const r = 60
    const cx = size / 2
    const cy = size / 2
    const pct = Math.min(100, Math.max(0, score))
    const startAngle = -90
    const endAngle = startAngle + (pct / 100) * 360
    const toRad = (deg) => (deg * Math.PI) / 180
    const x1 = cx + r * Math.cos(toRad(startAngle))
    const y1 = cy + r * Math.sin(toRad(startAngle))
    const x2 = cx + r * Math.cos(toRad(endAngle))
    const y2 = cy + r * Math.sin(toRad(endAngle))
    const largeArc = pct > 50 ? 1 : 0
    const arcPath = `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
            }}
        >
            <Box sx={{ position: 'relative', width: size, height: size }}>
                <svg width={size} height={size}>
                    {/* Colours as style: a CSS variable needs a CSS property,
    not an SVG attribute. */}
                    <circle
                        cx={cx}
                        cy={cy}
                        r={r}
                        fill="none"
                        strokeWidth="12"
                        style={{ stroke: SURFACE.sunken }}
                    />
                    {pct >= 100 ? (
                        <circle
                            cx={cx}
                            cy={cy}
                            r={r}
                            fill="none"
                            strokeWidth="12"
                            style={{
                                stroke: cfg.color,
                                transition: 'stroke 0.5s ease',
                            }}
                        />
                    ) : (
                        pct > 0 && (
                            <path
                                d={arcPath}
                                fill="none"
                                strokeWidth="12"
                                strokeLinecap="round"
                                style={{
                                    stroke: cfg.color,
                                    transition: 'd 0.8s ease, stroke 0.5s ease',
                                }}
                            />
                        )
                    )}
                </svg>
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Typography
                        variant="h1"
                        sx={{ color: cfg.color, lineHeight: 1 }}
                    >
                        {score}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{ color: TEXT.faint, mt: 0.25 }}
                    >
                        / 100
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                    sx={{
                        width: 28,
                        height: 28,
                        borderRadius: `${RADIUS.sm}px`,
                        // 8%: the light theme's success green is 4.4:1 on
                        // its own 12% tint (theme/contrast.ai-b.test.js).
                        background: tint(cfg.color, 0.08),
                        border: `1px solid ${tint(cfg.color, 0.35)}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Typography variant="h5" sx={{ color: cfg.color }}>
                        {grade}
                    </Typography>
                </Box>
                <Typography
                    variant="body2"
                    sx={{ color: TEXT.secondary, fontWeight: 600 }}
                >
                    {cfg.label}
                </Typography>
            </Box>
        </Box>
    )
}

function MetricStatCard({ label, value, benchmark, color }) {
    const numVal = typeof value === 'number' ? `${value}%` : value
    const aboveBench =
        benchmark && typeof value === 'number' && value > benchmark
    const belowBench =
        benchmark && typeof value === 'number' && value < benchmark
    return (
        <Paper
            elevation={0}
            sx={{
                ...cardSx,
                border: `1px solid ${tint(color, 0.3)}`,
                p: 2,
                textAlign: 'center',
            }}
        >
            <Typography variant="h1" sx={{ color, lineHeight: 1 }}>
                {numVal}
            </Typography>
            <Typography
                variant="caption"
                component="p"
                sx={{ color: TEXT.primary, fontWeight: 600, mt: 0.5 }}
            >
                {label}
            </Typography>
            {benchmark && (
                <Typography
                    variant="caption"
                    component="p"
                    sx={{
                        color: aboveBench
                            ? STATUS.success.fg
                            : belowBench
                            ? STATUS.error.fg
                            : TEXT.faint,
                        mt: 0.4,
                    }}
                >
                    Bench: {benchmark}%{' '}
                    {aboveBench ? '↑' : belowBench ? '↓' : '='}
                </Typography>
            )}
        </Paper>
    )
}

function InsightCard({ insight, index }) {
    const tone = IMPACT_TONE[insight.impact] || IMPACT_FALLBACK
    const icons = {
        positive: TrendingUpIcon,
        negative: WarningAmberIcon,
        neutral: LightbulbIcon,
    }
    const Icon = icons[insight.impact] || LightbulbIcon
    return (
        <Box
            sx={{
                display: 'flex',
                gap: 2,
                pb: 2,
                mb: 2,
                borderBottom: `1px solid ${SURFACE.border}`,
                animation: `${fadeIn} ${0.1 + index * 0.05}s ease`,
                '&:last-child': { borderBottom: 'none', mb: 0, pb: 0 },
            }}
        >
            <Box sx={{ flexShrink: 0, mt: '2px' }}>
                <Box
                    sx={{
                        width: 28,
                        height: 28,
                        borderRadius: `${RADIUS.sm}px`,
                        background: tone.bg,
                        border: `1px solid ${tone.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Icon sx={{ fontSize: ICON.sm, color: tone.fg }} />
                </Box>
            </Box>
            <Box sx={{ flex: 1 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.5,
                    }}
                >
                    <Typography variant="h6" sx={{ color: TEXT.primary }}>
                        {insight.title}
                    </Typography>
                    <Chip
                        label={insight.impact}
                        size="small"
                        sx={{
                            background: tone.bg,
                            color: tone.fg,
                            border: `1px solid ${tone.border}`,
                            typography: 'caption',
                            height: 18,
                            fontWeight: 700,
                            textTransform: 'capitalize',
                        }}
                    />
                </Box>
                <Typography
                    variant="caption"
                    component="p"
                    sx={{ color: TEXT.secondary, lineHeight: 1.6 }}
                >
                    {insight.detail}
                </Typography>
                {insight.action && (
                    <Typography
                        variant="caption"
                        component="p"
                        sx={{ color: tone.fg, mt: 0.5, fontStyle: 'italic' }}
                    >
                        {insight.action}
                    </Typography>
                )}
            </Box>
        </Box>
    )
}

export default function PerformanceInsights() {
    const navigate = useNavigate()
    const location = useLocation()
    const [campaignName, setCampaignName] = useState('')
    // Set when opened from a pipeline row; Open Pipeline goes back to it.
    const [pipelineID, setPipelineID] = useState(null)
    const [channel, setChannel] = useState('whatsapp')
    const [metrics, setMetrics] = useState({
        total_sent: '',
        delivered: '',
        opened: '',
        clicked: '',
        replied: '',
        unsubscribed: '',
    })
    const [loading, setLoading] = useState(false)
    const [result, setResult] = useState(null)
    const [error, setError] = useState('')
    const run = useToolRun('performance-insights')

    useEffect(() => {
        // Reset outputs so re-runs (navigation with new state) behave predictably
        setResult(null)
        setError('')
        const saved = run.recall()
        if (saved) {
            setCampaignName(saved.campaignName ?? '')
            setPipelineID(saved.pipelineID ?? null)
            setChannel(saved.channel)
            setMetrics(saved.metrics)
            setResult(saved.result)
            return
        }
        const state = location.state
        if (!state) return
        if (state.campaignName) setCampaignName(state.campaignName)
        setPipelineID(state.pipelineID ?? null)
        if (state.channel) setChannel(state.channel)
        if (state.metrics && typeof state.metrics === 'object')
            setMetrics((prev) => ({ ...prev, ...state.metrics }))
    }, [location.state])

    const updateMetric = (key, val) => {
        setMetrics((prev) => ({ ...prev, [key]: val.replace(/\D/g, '') }))
    }

    const isReady =
        campaignName.trim() && metrics.total_sent && metrics.delivered

    const handleAnalyse = async () => {
        if (!isReady) return
        setLoading(true)
        setError('')
        setResult(null)
        run.forget()
        try {
            const numericMetrics = {}
            Object.entries(metrics).forEach(([k, v]) => {
                if (v !== '') numericMetrics[k] = parseInt(v, 10)
            })
            const res = await AIService.performanceInsights({
                campaign_name: campaignName,
                channel,
                metrics: numericMetrics,
            })
            const analysed = res.data?.result
            setResult(analysed)
            if (analysed)
                run.remember({
                    campaignName,
                    pipelineID,
                    channel,
                    metrics,
                    result: analysed,
                })
        } catch (e) {
            setError(
                e?.response?.data?.message ||
                    'Failed to analyse performance. Please try again.'
            )
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box sx={pageSx}>
            <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 3, md: 5 } }}>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 3,
                    }}
                >
                    <Box
                        sx={{
                            background: GRADIENT.brand,
                            borderRadius: `${RADIUS.md}px`,
                            p: 1,
                            display: 'flex',
                        }}
                    >
                        {/* Deep Blue on the orange-to-yellow brand gradient (4.5:1 at its
    darkest end); white was 1.8:1 on the yellow. */}
                        <BarChartIcon
                            sx={{ color: ON_ORANGE, fontSize: ICON.lg }}
                        />
                    </Box>
                    <Box>
                        <Typography
                            variant="h3"
                            component="h1"
                            sx={{ color: TEXT.primary }}
                        >
                            Performance Insights
                        </Typography>
                        <Typography
                            variant="caption"
                            component="p"
                            sx={{ color: TEXT.muted }}
                        >
                            Paste your campaign metrics and get an AI-driven
                            analysis with benchmarks.
                        </Typography>
                    </Box>
                </Box>

                {/* Input form */}
                <Paper elevation={0} sx={{ ...cardSx, p: 2.5, mb: 3 }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
                            gap: 2,
                            mb: 2.5,
                        }}
                    >
                        <TextField
                            label="Campaign Name"
                            value={campaignName}
                            onChange={(e) => setCampaignName(e.target.value)}
                            placeholder="e.g. Batch Re-engagement — June 2025"
                        />
                        <TextField
                            select
                            label="Channel"
                            value={channel}
                            onChange={(e) => setChannel(e.target.value)}
                            sx={{ minWidth: 160 }}
                            SelectProps={{ MenuProps: menuProps }}
                        >
                            {CHANNELS.map(({ value, label, Icon }) => (
                                <MenuItem key={value} value={value}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <Icon sx={{ fontSize: ICON.sm }} />
                                        {label}
                                    </Box>
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    <Typography
                        variant="overline"
                        component="p"
                        sx={{ color: TEXT.faint, mb: 1.5 }}
                    >
                        Campaign Metrics
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr 1fr',
                                sm: 'repeat(3, 1fr)',
                            },
                            gap: 2,
                        }}
                    >
                        {METRIC_FIELDS.map(({ key, label, placeholder }) => (
                            <TextField
                                key={key}
                                label={label}
                                value={metrics[key]}
                                onChange={(e) =>
                                    updateMetric(key, e.target.value)
                                }
                                placeholder={placeholder}
                                size="small"
                            />
                        ))}
                    </Box>

                    <Button
                        variant="contained"
                        fullWidth
                        onClick={handleAnalyse}
                        disabled={loading || !isReady}
                        startIcon={
                            loading ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <BarChartIcon />
                            )
                        }
                        sx={{ mt: 2.5, py: 1.25 }}
                    >
                        {loading ? 'Analysing…' : 'Analyse Performance'}
                    </Button>
                </Paper>

                {error && (
                    <Box sx={{ mb: 2 }}>
                        <ErrorBanner message={error} />
                    </Box>
                )}

                {loading && (
                    <Box sx={{ display: 'grid', gap: 2 }}>
                        <ShimmerBlock height={80} />
                        <ShimmerBlock height={180} />
                        <ShimmerBlock height={240} />
                    </Box>
                )}

                {result && (
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 2,
                            animation: `${fadeIn} 0.3s ease`,
                        }}
                    >
                        {/* Score + rates row */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'auto 1fr',
                                },
                                gap: 2,
                                alignItems: 'start',
                            }}
                        >
                            <Paper
                                elevation={0}
                                sx={{
                                    ...cardSx,
                                    p: 3,
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}
                            >
                                <ScoreGauge
                                    score={result.performance_score}
                                    grade={result.grade}
                                />
                            </Paper>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    gap: 2,
                                }}
                            >
                                {result.delivery_rate !== undefined && (
                                    <MetricStatCard
                                        label="Delivery Rate"
                                        value={result.delivery_rate}
                                        benchmark={
                                            result.benchmarks?.delivery_rate
                                        }
                                        color={RATE_COLOR.delivery}
                                    />
                                )}
                                {result.open_rate !== undefined && (
                                    <MetricStatCard
                                        label="Open / Read Rate"
                                        value={result.open_rate}
                                        benchmark={result.benchmarks?.open_rate}
                                        color={RATE_COLOR.open}
                                    />
                                )}
                                {result.click_rate !== undefined && (
                                    <MetricStatCard
                                        label="Click Rate"
                                        value={result.click_rate}
                                        benchmark={
                                            result.benchmarks?.click_rate
                                        }
                                        color={RATE_COLOR.click}
                                    />
                                )}
                                {result.reply_rate !== undefined && (
                                    <MetricStatCard
                                        label="Reply Rate"
                                        value={result.reply_rate}
                                        benchmark={
                                            result.benchmarks?.reply_rate
                                        }
                                        color={RATE_COLOR.reply}
                                    />
                                )}
                                {result.unsubscribe_rate !== undefined && (
                                    <MetricStatCard
                                        label="Unsub Rate"
                                        value={result.unsubscribe_rate}
                                        benchmark={
                                            result.benchmarks?.unsubscribe_rate
                                        }
                                        color={RATE_COLOR.unsubscribe}
                                    />
                                )}
                            </Box>
                        </Box>

                        {/* Insights */}
                        {result.insights && result.insights.length > 0 && (
                            <Paper elevation={0} sx={{ ...cardSx, p: 2.5 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mb: 2,
                                    }}
                                >
                                    <LightbulbIcon
                                        sx={{
                                            fontSize: ICON.md,
                                            color: BRAND.orange,
                                        }}
                                    />
                                    <Typography
                                        variant="overline"
                                        sx={{ color: TEXT.muted }}
                                    >
                                        AI Insights
                                    </Typography>
                                </Box>
                                {result.insights.map((ins, i) => (
                                    <InsightCard
                                        key={i}
                                        insight={ins}
                                        index={i}
                                    />
                                ))}
                            </Paper>
                        )}

                        {/* Recommendations */}
                        {result.recommendations &&
                            result.recommendations.length > 0 && (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        background: ACCENT.bg08,
                                        border: `1px solid ${ACCENT.border30}`,
                                        borderRadius: `${RADIUS.lg}px`,
                                        p: 2.5,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 1.5,
                                        }}
                                    >
                                        {/* ACCENT.strong: a mark on the orange tint, where full orange is
    under 3:1 in the light theme. */}
                                        <TrendingUpIcon
                                            sx={{
                                                fontSize: ICON.md,
                                                color: ACCENT.strong,
                                            }}
                                        />
                                        <Typography
                                            variant="overline"
                                            sx={{ color: ACCENT.text }}
                                        >
                                            Recommendations
                                        </Typography>
                                    </Box>
                                    {result.recommendations.map((r, i) => (
                                        <Box
                                            key={i}
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                                mb: 0.75,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: ACCENT.text,
                                                    mt: '1px',
                                                }}
                                            >
                                                •
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: TEXT.secondary,
                                                    lineHeight: 1.6,
                                                }}
                                            >
                                                {r}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Paper>
                            )}

                        {/* Summary */}
                        {result.summary && (
                            <Paper elevation={0} sx={{ ...cardSx, p: 2.5 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: TEXT.secondary,
                                        lineHeight: 1.75,
                                        fontStyle: 'italic',
                                    }}
                                >
                                    {result.summary}
                                </Typography>
                            </Paper>
                        )}

                        {/* CTA row */}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                mt: 2,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AssessmentIcon />}
                                onClick={() => navigate('/reports')}
                            >
                                View Full Report
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<PolylineIcon />}
                                // The pipeline these insights were opened from,
                                // or the pipeline list when the name was typed
                                // here (it went to "/", the home page).
                                onClick={() =>
                                    navigate(
                                        pipelineID
                                            ? `/pipelines/edit/${encodeURIComponent(
                                                  pipelineID
                                              )}`
                                            : '/pipelines'
                                    )
                                }
                            >
                                Open Pipeline
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    )
}
